import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dumbbell, Trash2, Plus, Copy, X } from "lucide-react";

const RESULT_TYPES = [
  ["load", { es: "Carga (lbs)", en: "Load (lbs)", fr: "Charge (lbs)" }],
  ["time", { es: "Tiempo", en: "Time", fr: "Temps" }],
  ["reps", { es: "Reps", en: "Reps", fr: "Répétitions" }],
  ["free", { es: "Libre", en: "Free", fr: "Libre" }],
];

const blank = () => ({ program_id: null, client_id: "", title: "", description: "", weeks: [] });
const uid = () => crypto.randomUUID();
// Add stable _uid at every level for React keys (editable nested lists).
const withUids = (weeks) => (weeks || []).map((w) => ({
  ...w, _uid: w._uid || uid(),
  days: (w.days || []).map((d) => ({
    ...d, _uid: d._uid || uid(),
    blocks: (d.blocks || []).map((b) => ({
      ...b, _uid: b._uid || uid(),
      videos: (b.videos || []).map((v) => ({ ...v, _uid: v._uid || uid() })),
    })),
  })),
}));
// Strip _uid recursively before sending to the API.
const stripUids = (weeks) => (weeks || []).map(({ _uid, days, ...w }) => ({
  ...w,
  days: (days || []).map(({ _uid, blocks, ...d }) => ({
    ...d,
    blocks: (blocks || []).map(({ _uid, videos, ...b }) => ({
      ...b,
      videos: (videos || []).map(({ _uid, ...v }) => v),
    })),
  })),
}));

export function AdminPrograms() {
  const { t, tl } = useI18n();
  const [clients, setClients] = useState([]);
  const [videos, setVideos] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [f, setF] = useState(blank());

  const loadPrograms = () => api.get("/admin/programs").then(({ data }) => setPrograms(data));
  useEffect(() => {
    api.get("/admin/clients").then(({ data }) => setClients(data.filter((c) => c.is_online)));
    api.get("/videos").then(({ data }) => setVideos(data));
    loadPrograms();
  }, []);

  const mutate = (fn) => setF((prev) => { const w = structuredClone(prev.weeks); fn(w); return { ...prev, weeks: w }; });
  const addWeek = () => mutate((w) => w.push({ _uid: uid(), label: `${tl({ es: "Semana", en: "Week", fr: "Semaine" })} ${w.length + 1}`, days: [] }));
  const removeWeek = (wi) => mutate((w) => w.splice(wi, 1));
  const addDay = (wi) => mutate((w) => w[wi].days.push({ _uid: uid(), label: `${tl({ es: "Día", en: "Day", fr: "Jour" })} ${w[wi].days.length + 1}`, coach_note: "", blocks: [] }));
  const removeDay = (wi, di) => mutate((w) => w[wi].days.splice(di, 1));
  const addBlock = (wi, di) => mutate((w) => w[wi].days[di].blocks.push({ _uid: uid(), title: "", description: "", result_type: "load", videos: [] }));
  const removeBlock = (wi, di, bi) => mutate((w) => w[wi].days[di].blocks.splice(bi, 1));
  const addVideo = (wi, di, bi, v) => mutate((w) => w[wi].days[di].blocks[bi].videos.push({ _uid: uid(), video_id: v.video_id, title: v.title, url: v.url }));
  const removeVideo = (wi, di, bi, vi) => mutate((w) => w[wi].days[di].blocks[bi].videos.splice(vi, 1));

  const save = async () => {
    if (!f.client_id || !f.title) { toast.error(tl({ es: "Cliente y título requeridos", en: "Client and title required", fr: "Client et titre requis" })); return; }
    try {
      await api.post("/admin/programs", f);
      toast.success(t("common.success"));
      setF(blank()); loadPrograms();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const editProg = async (p) => { const { data } = await api.get(`/admin/programs/${p.program_id}`); setF({ ...data.program }); window.scrollTo(0, 0); };
  const del = async (id) => { try { await api.delete(`/admin/programs/${id}`); loadPrograms(); if (f.program_id === id) setF(blank()); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const duplicate = async (p) => { try { await api.post(`/admin/programs/${p.program_id}/duplicate`, {}); toast.success(t("common.success")); loadPrograms(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <AdminLayout>
      <h1 className="mb-6 flex items-center gap-2 font-display text-3xl font-bold tracking-tight">
        <Dumbbell className="h-7 w-7 text-primary" /> {tl({ es: "Coaching online", en: "Online coaching", fr: "Coaching en ligne" })}
      </h1>

      <div className="grid gap-6 xl:grid-cols-3">
        {/* Builder */}
        <div className="xl:col-span-2 rounded-2xl border border-border bg-card p-6">
          <h2 className="mb-4 font-display text-lg font-semibold">{f.program_id ? tl({ es: "Editar programa", en: "Edit program", fr: "Modifier le programme" }) : tl({ es: "Nuevo programa", en: "New program", fr: "Nouveau programme" })}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>{tl({ es: "Cliente en línea", en: "Online client", fr: "Client en ligne" })}</Label>
              <select data-testid="prog-client" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} className="mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3">
                <option value="">—</option>
                {clients.map((c) => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
              </select>
              {clients.length === 0 && <p className="mt-1 text-xs text-destructive">{tl({ es: "Activa 'En línea' en un cliente primero.", en: "Enable 'Online' on a client first.", fr: "Activez d'abord « En ligne » sur un client." })}</p>}
            </div>
            <div><Label>{tl({ es: "Título", en: "Title", fr: "Titre" })}</Label><Input data-testid="prog-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1.5 h-11" /></div>
          </div>
          <div className="mt-3"><Label>{tl({ es: "Descripción", en: "Description", fr: "Description" })}</Label><Textarea data-testid="prog-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} className="mt-1.5" /></div>

          {/* Weeks */}
          <div className="mt-5 space-y-4">
            {f.weeks.map((w, wi) => (
              <div key={w._uid} data-testid={`week-${wi}`} className="rounded-xl border border-royal/40 bg-royal/5 p-4">
                <div className="flex items-center gap-2">
                  <Input data-testid={`week-label-${wi}`} value={w.label} onChange={(e) => mutate((ws) => { ws[wi].label = e.target.value; })} className="h-9 font-semibold" />
                  <button data-testid={`remove-week-${wi}`} onClick={() => removeWeek(wi)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
                </div>
                {/* Days */}
                <div className="mt-3 space-y-3">
                  {w.days.map((d, di) => (
                    <div key={d._uid} data-testid={`day-${wi}-${di}`} className="rounded-lg border border-border bg-background/60 p-3">
                      <div className="flex items-center gap-2">
                        <Input data-testid={`day-label-${wi}-${di}`} value={d.label} onChange={(e) => mutate((ws) => { ws[wi].days[di].label = e.target.value; })} className="h-9 font-medium" />
                        <button data-testid={`remove-day-${wi}-${di}`} onClick={() => removeDay(wi, di)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
                      </div>
                      <div className="mt-2">
                        <Label className="text-xs">Coach note</Label>
                        <Textarea data-testid={`coach-note-${wi}-${di}`} value={d.coach_note} onChange={(e) => mutate((ws) => { ws[wi].days[di].coach_note = e.target.value; })} rows={2} className="mt-1" placeholder={tl({ es: "Consignas para el cliente...", en: "Instructions for the client...", fr: "Consignes pour le client..." })} />
                      </div>
                      {/* Blocks */}
                      <div className="mt-3 space-y-2">
                        {d.blocks.map((b, bi) => (
                          <div key={b._uid} data-testid={`block-${wi}-${di}-${bi}`} className="rounded-lg border border-border p-3">
                            <div className="flex items-center gap-2">
                              <Input data-testid={`block-title-${wi}-${di}-${bi}`} value={b.title} onChange={(e) => mutate((ws) => { ws[wi].days[di].blocks[bi].title = e.target.value; })} placeholder={tl({ es: "Título del bloque", en: "Block title", fr: "Titre du bloc" })} className="h-9" />
                              <select data-testid={`block-type-${wi}-${di}-${bi}`} value={b.result_type} onChange={(e) => mutate((ws) => { ws[wi].days[di].blocks[bi].result_type = e.target.value; })} className="h-9 rounded-lg border border-border bg-background px-2 text-sm">
                                {RESULT_TYPES.map(([v, lbl]) => <option key={v} value={v}>{tl(lbl)}</option>)}
                              </select>
                              <button data-testid={`remove-block-${wi}-${di}-${bi}`} onClick={() => removeBlock(wi, di, bi)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
                            </div>
                            <Textarea data-testid={`block-desc-${wi}-${di}-${bi}`} value={b.description} onChange={(e) => mutate((ws) => { ws[wi].days[di].blocks[bi].description = e.target.value; })} rows={2} className="mt-2" placeholder="5x5 @ 80%..." />
                            {/* attached videos */}
                            {(b.videos || []).length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {b.videos.map((v, vi) => (
                                  <span key={v._uid || vi} className="flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs">
                                    {v.title || v.url}
                                    <button data-testid={`remove-video-${wi}-${di}-${bi}-${vi}`} onClick={() => removeVideo(wi, di, bi, vi)}><X className="h-3 w-3" /></button>
                                  </span>
                                ))}
                              </div>
                            )}
                            <select data-testid={`add-video-${wi}-${di}-${bi}`} value="" onChange={(e) => { const v = videos.find((x) => x.video_id === e.target.value); if (v) addVideo(wi, di, bi, v); }} className="mt-2 h-9 w-full rounded-lg border border-border bg-background px-2 text-sm">
                              <option value="">+ {tl({ es: "Añadir video de la biblioteca", en: "Add video from library", fr: "Ajouter une vidéo de la bibliothèque" })}</option>
                              {videos.map((v) => <option key={v.video_id} value={v.video_id}>{v.title} ({v.category})</option>)}
                            </select>
                          </div>
                        ))}
                        <Button size="sm" variant="outline" data-testid={`add-block-${wi}-${di}`} onClick={() => addBlock(wi, di)} className="rounded-full"><Plus className="mr-1 h-3.5 w-3.5" />{tl({ es: "Bloque", en: "Block", fr: "Bloc" })}</Button>
                      </div>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" data-testid={`add-day-${wi}`} onClick={() => addDay(wi)} className="rounded-full"><Plus className="mr-1 h-3.5 w-3.5" />{tl({ es: "Día", en: "Day", fr: "Jour" })}</Button>
                </div>
              </div>
            ))}
            <Button variant="outline" data-testid="add-week" onClick={addWeek} className="w-full rounded-full"><Plus className="mr-1.5 h-4 w-4" />{tl({ es: "Añadir semana", en: "Add week", fr: "Ajouter une semaine" })}</Button>
          </div>

          <div className="mt-5 flex gap-2">
            <Button data-testid="prog-save" onClick={save} className="rounded-full">{t("common.save")}</Button>
            {f.program_id && <Button variant="ghost" data-testid="prog-new" onClick={() => setF(blank())} className="rounded-full">{tl({ es: "Nuevo", en: "New", fr: "Nouveau" })}</Button>}
          </div>
        </div>

        {/* List */}
        <div>
          <h2 className="mb-4 font-display text-lg font-semibold">{tl({ es: "Programas", en: "Programs", fr: "Programmes" })}</h2>
          <div className="space-y-2">
            {programs.length === 0 && <p className="text-sm text-muted-foreground">{t("common.noData")}</p>}
            {programs.map((p) => (
              <div key={p.program_id} data-testid={`program-item-${p.program_id}`} className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs text-muted-foreground">{p.client_name} · {(p.weeks || []).length} {tl({ es: "semanas", en: "weeks", fr: "semaines" })}</div>
                <div className="font-medium">{p.title}</div>
                <div className="mt-2 flex gap-1.5">
                  <Button size="sm" variant="outline" data-testid={`edit-program-${p.program_id}`} onClick={() => editProg(p)} className="rounded-full">{tl({ es: "Editar", en: "Edit", fr: "Modifier" })}</Button>
                  <Button size="sm" variant="ghost" data-testid={`dup-program-${p.program_id}`} onClick={() => duplicate(p)} className="rounded-full"><Copy className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" data-testid={`del-program-${p.program_id}`} onClick={() => del(p.program_id)} className="rounded-full text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
